/* eslint-disable no-console */
import { capitalizeString } from '../../../utils/commonHelper';
import {getSubdivision, getLocation, getPrice} from '../../../utils/listingHelper';
import { getBoatUrl } from '../../../utils/urlHelpers/boat';
import { getEngineUrl } from '../../../utils/urlHelpers/engine';
import messages from '../../../utils/translationMessages';
import { CUSTOM_META_DESCRIPTION_LENGTH } from '../../../constants/index';
import { formatMessage as t, getCurrentLocale } from '../../../utils/language';
import get from 'lodash/get';
import { urlFromLanguage } from '../../../utils/seo';
import { getLength } from '../../../utils/listingHelper';

const RULES = [
  {
    name: 'length24m',
    evaluate: (listing) => {
      const boatLengthMeters = get(listing, 'specifications.dimensions.lengths.nominal.m', 0);
      const { year = '', boatName, class: boatClass, make = '', model = '' } = listing;
      const parameters = {
        year, make, model,
        boatClass: boatClass ? t(messages.classFacetValues[boatClass]) : ''
      };
      if (boatLengthMeters >= CUSTOM_META_DESCRIPTION_LENGTH) {
        if (boatName && boatName.trim().length) {
          return t(
            messages.boatDetails.SEO.metaElements.title.length24m.boatName,
            { ...parameters, boatName: boatName.trim() }
          ).trim();
        }

        return t(messages.boatDetails.SEO.metaElements.title.length24m.true, parameters).trim();
      }
      return t(messages.boatDetails.SEO.metaElements.title.length24m.false, parameters).trim();
    }
  },
  {
    name: 'boatType',
    evaluate: (listing, previousValue, customUom) => {
      const {
        condition, model, year, make,
        type: boatType = 'power'
      } = listing;
      const length = getLength(listing, undefined, customUom?.length, true);
      const className = (listing.class && messages.classFacetValues[listing.class]) ? t(messages.classFacetValues[listing.class]) : '';
      const price = getPrice(listing);
      const address = getLocation(listing);
      const params = {
        condition: capitalizeString(t(messages[condition])),
        year,
        make,
        model,
        length,
        address,
        price,
        class: className
      };
      return t(messages.boatDetails.SEO.metaElements.title[boatType], params);
    }
  },
  {
    name: 'subdivision',
    evaluate: (listing, previousValue) => {
      const subdivision = getSubdivision(listing);
      if (subdivision && subdivision.trim().length) {
        previousValue += t(messages.boatDetails.SEO.metaElements.title.subdivision, { subdivision });
        previousValue = previousValue.trim();
      }
      return previousValue;
    }
  },
  {
    name: 'engine',
    evaluate: (listing) => {
      const { condition, model, year, make } = listing;
      const price = getPrice(listing);
      const address = getLocation(listing);
      const params = {
        condition: capitalizeString(t(messages[condition])),
        year,
        make,
        model,
        address,
        price,
      };
      params.city = capitalizeString(get(listing, 'location.address.city', ''));
      if (listing.power?.hp) {
        params.power = `${listing.power.hp}${t(messages.uom.hp)}`;
      } else if (listing.power?.kW) {
        params.power = `${listing.power.kW}${t(messages.uom.kW)}`;
      }
      return t(messages.engineDetails.SEO.metaElements.title, params);
    }
  }
];
const defaultSEOConfig = {
  title: {
    conditions: ['boatType', 'subdivision']
  }
};

export const getMetaTitle = (listing = {}, portal, seoConfig = defaultSEOConfig, customUom) => {
  if (listing.id) {
    let title = '';
    title = RULES;
    title = title.filter(({ name: conditionName }) => seoConfig.title.conditions.includes(conditionName));
    title = title.reduce((resultTitle, condition) => {
      return condition.evaluate(listing, resultTitle, customUom);
    }, title);
    title += t(messages.boatDetails.SEO.metaElements.title.portalName, { portal });
    return title;
  }

  return `Boat Details Title | ${portal}`;
};

export const getMetaDescription = (listing = {}, portal, customMeta, options = {}) => {
  let description = 'Boat Details Description';

  if (listing.id) {
    const boatLengthMeters = get(listing, 'specifications.dimensions.lengths.nominal.m', 0);
    const boatClass = get(listing, 'class', '');
    const { condition } = listing;

    const translationParameters = {
      model: get(listing, 'model', ''),
      name: get(listing, 'boatName', '').trim(),
      class: boatClass ? t(messages.classFacetValues[boatClass]) : '',
      condition: condition ? capitalizeString(t(messages[condition])) : '',
      location: getLocation(listing),
      year: listing.year,
      make: listing.make,
      portal
    };

    if (customMeta) {
      description = boatLengthMeters >= CUSTOM_META_DESCRIPTION_LENGTH
        ? (translationParameters.name !== ''
          ? t(messages.boatDetails.SEO.metaElements.descriptionLargeYacht, { ...translationParameters })
          : t(messages.boatDetails.SEO.metaElements.descriptionLargeYachtNoName, { ...translationParameters }))
        : (translationParameters.name !== ''
          ? t(messages.boatDetails.SEO.metaElements.descriptionSmallYacht, { ...translationParameters })
          : t(messages.boatDetails.SEO.metaElements.descriptionSmallYachtNoName, { ...translationParameters }));
    }
    else {
      description = t(messages.boatDetails.SEO.metaElements.description, { ...translationParameters });
    }

    if (options.pageType === 'edp') {
      // Engines Details Page:
      description = t(messages.engineDetails.SEO.metaElements.description, { ...translationParameters });
    }
  }

  return description;
};

export const getNoIndex = () => {
  return false;
};

export const getCanonical = (listing = {}, isEngine = false) => {
  const locale = getCurrentLocale();
  if (listing.id) {
    return urlFromLanguage((isEngine ? getEngineUrl(listing) : getBoatUrl(listing)), locale);
  }
  return urlFromLanguage('/', locale);
};

export const getCanonicalEngine = (listing = {}) => {
  return getCanonical(listing, true);
};

export const getLinkRelPrev = () => {
  return '';
};

export const getLinkRelNext = () => {
  return '';
};
